import { html, css, nothing } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { endpoints, ApiError } from '../api/client.js';
import { navigate } from '../router.js';
import type { TwoFactorResult } from '../api/types.js';
import type { SsoCodeInput } from '../components/sso-code-input.js';
import type { SsoTextField } from '../components/sso-text-field.js';
import '../components/sso-card.js';
import '../components/sso-code-input.js';
import '../components/sso-text-field.js';
import '../components/sso-button.js';
import '../components/sso-alert.js';

/**
 * `<totp-entry-page>` — 6-digit authenticator code entry (SSO-MOCKUPS.md §3).
 *
 * Doubles as the recovery-code entry screen via `?recovery=1` (a single free-text field).
 * On success the server returns `{ next }` or a terminal `{ redirect }`. Failures show a
 * generic message; the server enforces throttling.
 */
@customElement('totp-entry-page')
@localized()
export class TotpEntryPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      h1 {
        text-align: center;
      }
      p {
        text-align: center;
      }
      sso-code-input {
        margin: var(--sso-space) 0;
      }
      .actions {
        display: grid;
        gap: var(--sso-space);
        margin-top: var(--sso-space);
      }
      .links {
        display: grid;
        gap: 0.5rem;
        margin-top: var(--sso-space);
        text-align: center;
        font-size: 0.9rem;
      }
    `,
  ];

  @state() private recovery = false;
  @state() private error = '';
  @state() private busy = false;

  @query('sso-code-input') private codeInput?: SsoCodeInput;
  @query('sso-text-field') private recoveryField?: SsoTextField;

  private readonly onPop = (): void => {
    this.recovery = new URLSearchParams(location.search).get('recovery') === '1';
    this.error = '';
  };

  override connectedCallback(): void {
    super.connectedCallback();
    this.onPop();
    window.addEventListener('popstate', this.onPop);
  }

  override disconnectedCallback(): void {
    window.removeEventListener('popstate', this.onPop);
    super.disconnectedCallback();
  }

  override firstUpdated(): void {
    // Focus the first entry control (code boxes, or the recovery field) so the user can type at once.
    this.focusFirstControl();
  }

  /** Verify a TOTP code (from the split input's `complete` event). */
  private async verifyTotp(code: string): Promise<void> {
    await this.submit(() => endpoints.loginTotp(code));
  }

  /** Verify a recovery code (manual single field). */
  private async verifyRecovery(): Promise<void> {
    const code = (this.recoveryField?.value ?? '').trim();
    if (!code) {
      this.error = msg('Please enter a recovery code.');
      return;
    }
    await this.submit(() => endpoints.loginRecovery(code));
  }

  private async submit(call: () => Promise<TwoFactorResult>): Promise<void> {
    this.busy = true;
    this.error = '';
    try {
      const r = await call();
      if (r.next === 'complete') {
        this.complete();
        return;
      }
      this.error = msg('That code was not accepted. Please try again.');
      this.codeInput?.reset();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        this.error = msg('Too many attempts. Please wait and try again.');
      } else {
        this.error = msg('That code was not accepted. Please try again.');
      }
      this.codeInput?.reset();
    } finally {
      this.busy = false;
    }
  }

  /** Second factor satisfied — follow the launcher-initiated return target if any, else launcher. */
  private complete(): void {
    const returnUri = this.resolveReturnUri();
    if (returnUri) {
      this.redirect(returnUri);
      return;
    }
    navigate('/');
  }

  private renderTotp() {
    return html`
      <h1>${msg('Authenticator code')}</h1>
      <p>${msg('Enter the 6-digit code from your authenticator app.')}</p>
      ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}
      <sso-code-input
        length="6"
        ?disabled=${this.busy}
        aria-label=${msg('6-digit code from your authenticator app')}
        @complete=${(e: CustomEvent<string>) => this.verifyTotp(e.detail)}></sso-code-input>
      <div class="links">
        <a
          href="/2fa/totp?recovery=1"
          @click=${(e: Event) => { e.preventDefault(); navigate('/2fa/totp?recovery=1'); }}>
          ${msg('No access to your app? Use a recovery code')}
        </a>
        <a href="/logout">${msg('Cancel / Sign out')}</a>
      </div>
    `;
  }

  private renderRecovery() {
    return html`
      <h1>${msg('Recovery code')}</h1>
      <p>${msg('Enter one of your one-time recovery codes.')}</p>
      ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}
      <sso-text-field
        label=${msg('Recovery code')}
        autocomplete="one-time-code"
        ?disabled=${this.busy}></sso-text-field>
      <div class="actions">
        <sso-button variant="primary" ?busy=${this.busy} @click=${this.verifyRecovery}>
          ${msg('Verify')}
        </sso-button>
      </div>
      <div class="links">
        <a
          href="/2fa/totp"
          @click=${(e: Event) => { e.preventDefault(); navigate('/2fa/totp'); }}>
          ${msg('Use an authenticator code instead')}
        </a>
        <a href="/logout">${msg('Cancel / Sign out')}</a>
      </div>
    `;
  }

  override render() {
    return html`<sso-card>${this.recovery ? this.renderRecovery() : this.renderTotp()}</sso-card>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'totp-entry-page': TotpEntryPage;
  }
}
